/**
 * Evolve Drywall — customer site content config.
 * This is the SAME shape the future Website Builder (admin UI) will edit.
 * Anything not here falls back to template defaults.
 */
export const evolveDrywallConfig = {
  slug: 'evolvedrywall',
  business_name: 'Evolve Drywall',
  industry: 'drywall',
  template_id: 'contractor-basic',
  custom_domain: 'www.evolvedrywall.com',
  // Loaded from backend for production; keep here for offline/preview parity
  phone_number: '+18313287100',
  phone_display: '(831) 328-7100',
  owner_email_public: 'estimates@evolvedrywall.com',
  city: 'Sacramento, CA',
  service_area: 'Sacramento, CA + surrounding areas',
  service_areas: [
    'Sacramento',
    'Roseville',
    'Folsom',
    'Elk Grove',
    'Rocklin',
    'Davis',
    'Citrus Heights',
    'Rancho Cordova',
  ],
  founded_year: 2017,
  hours: 'Mon–Sat 7:00 AM – 6:00 PM',

  brand: {
    primary: '#0e0e0e',
    primary_dark: '#000000',
    accent: '#b8924b',
    accent_dark: '#8a6b2f',
    neutral_bg: '#f5f3ee',
    text_dark: '#0b0b0b',
    logo_text: 'Evolve Drywall',
  },

  logo: {
    // Wordmark for light backgrounds (nav)
    wordmark_light: '/evolve-wordmark-compact.png',
    // Transparent wordmark for dark backgrounds
    wordmark_transparent: '/evolve-wordmark-transparent.png',
    // ED monogram (transparent — works on light or dark)
    mark: '/evolve-mark-transparent-512.png',
    // Favicons
    favicon_ico: '/evolve-favicon.ico',
    favicon_32: '/evolve-favicon-32.png',
    favicon_192: '/evolve-favicon-192.png',
    favicon_512: '/evolve-favicon-512.png',
    apple_touch_icon: '/evolve-favicon-192.png',
  },

  seo: {
    title: 'Evolve Drywall — Drywall Contractor in Sacramento, CA | Free Estimates',
    description:
      'Professional drywall installation, repair, finishing and texture matching across Sacramento and surrounding areas. Free estimates, fast turnaround, clean, dust-controlled work.',
    canonical: 'https://www.evolvedrywall.com/',
    og_image: '',
    keywords: [
      'drywall contractor sacramento',
      'drywall repair sacramento',
      'drywall installation',
      'drywall finishing',
      'drywall texture matching',
      'drywall estimate',
    ],
  },

  hero: {
    eyebrow: 'Sacramento drywall contractor · est. 2017',
    headline: 'Quality finishes. Built to Evolve.',
    subhead:
      'Evolve Drywall handles installation, repair, finishing and texture matching for homeowners and contractors across Sacramento and surrounding areas.',
    primary_cta: { label: 'Request a Free Estimate', href: '#estimate' },
    secondary_cta: { label: 'Call (831) 328-7100', href: 'tel:+18313287100' },
    badges: ['Licensed & Insured', 'Free On-Site Estimates', 'Locally Owned'],
  },

  services: [
    {
      title: 'Drywall Installation',
      description:
        'New construction, additions, garage conversions and remodels. Hung, taped and finished to a level-4 or level-5 finish on request.',
      icon: 'Hammer',
    },
    {
      title: 'Drywall Repair',
      description:
        'Patch holes, water damage, settling cracks and popped nails. We blend repairs into the surrounding wall so they disappear.',
      icon: 'Wrench',
    },
    {
      title: 'Texture Matching',
      description:
        'Knockdown, orange peel, smooth and hand-trowel textures matched to the rest of your home — no obvious patch lines.',
      icon: 'Sparkles',
    },
    {
      title: 'Finishing & Skim Coat',
      description:
        'Level-5 skim coats, accent walls and prep for paint. Smooth, paint-ready surfaces with minimal sanding dust.',
      icon: 'Layers',
    },
    {
      title: 'Acoustic Ceiling Removal',
      description:
        'Popcorn ceiling removal, scrape, prime and refinish — turn dated ceilings into clean, modern surfaces.',
      icon: 'CloudOff',
    },
    {
      title: 'Commercial Drywall',
      description:
        'Tenant improvements, office build-outs and small commercial projects with predictable turnaround windows.',
      icon: 'Building2',
    },
  ],

  why_us: [
    {
      title: 'Clean job sites',
      body: 'Plastic barriers, dust containment and daily cleanup so your home stays livable while we work.',
    },
    {
      title: 'Honest, fixed estimates',
      body: 'Every estimate is itemized and fixed in writing — no surprise change orders if the scope is what we measured.',
    },
    {
      title: 'Real craftsmanship',
      body: 'Texture matching is the part most contractors rush. We slow down, sample, and blend until it disappears.',
    },
  ],

  projects: [
    { title: 'Whole-home repaint prep, East Sacramento', tag: 'Skim coat · Level 5' },
    { title: 'Garage-to-office conversion, Folsom', tag: 'Install · Texture match' },
    { title: 'Water damage restoration, Roseville', tag: 'Repair · Texture' },
    { title: 'Popcorn ceiling removal, Rocklin', tag: 'Acoustic removal' },
    { title: 'New addition framing close-out, Elk Grove', tag: 'New install' },
    { title: 'Commercial TI, Midtown Sacramento', tag: 'Commercial' },
  ],

  about: {
    headline: 'A drywall crew you’d actually recommend to your neighbor.',
    body:
      'Evolve Drywall was founded in 2017 by hangers and finishers who got tired of seeing the same corners cut on every job — sloppy mud, mismatched texture, and quick patches that telegraph through paint a year later. We built Evolve to be the opposite: small enough to care, experienced enough to do it right the first time.',
    stats: [
      { value: '8+', label: 'Years in business' },
      { value: '1,200+', label: 'Projects completed' },
      { value: '4.9★', label: 'Average review' },
    ],
  },

  faq: [
    {
      q: 'How fast can you start?',
      a: 'Most repairs and small jobs can start within 3–5 business days. Larger installs are typically scheduled 1–2 weeks out. Emergencies (water damage) move to the front of the queue when possible.',
    },
    {
      q: 'Do you give free estimates?',
      a: 'Yes. On-site estimates are free across our service area. For very small repairs we can often quote from a few photos by text or email.',
    },
    {
      q: 'Are you licensed and insured?',
      a: 'Yes — fully licensed in California and carrying general liability + workers comp. We can email proof of insurance to your project address before we start.',
    },
    {
      q: 'Will my house be a mess afterwards?',
      a: 'No. We use plastic barriers, ZipWalls and HEPA vacuums to contain dust. Job sites are broom-clean at the end of every day, not just the end of the project.',
    },
    {
      q: 'Can you match an existing texture?',
      a: 'Yes — knockdown, orange peel, smooth and hand-trowel are our most common matches. We sample on a scrap board first so the texture, paint sheen and edge feathering all blend before we touch the finished wall.',
    },
  ],

  cta_banner: {
    headline: 'Ready for smooth, clean walls?',
    body: 'Get a free, no-pressure estimate from a real drywall crew — usually back to you the same day.',
    primary: { label: 'Request a Free Estimate', href: '#estimate' },
    secondary: { label: 'Call (831) 328-7100', href: 'tel:+18313287100' },
  },

  estimate_form: {
    title: 'Request a Free Estimate',
    subtitle: 'Tell us about your project and we’ll get back to you the same day.',
    project_types: [
      'Drywall repair',
      'Drywall installation',
      'Texture matching',
      'Skim coat / level 5',
      'Popcorn ceiling removal',
      'Water damage repair',
      'Commercial / TI',
      'Other',
    ],
    success_message:
      'Thanks — your request is in. We’ll text or call you within a few business hours to schedule a free estimate.',
  },

  footer: {
    tagline: 'Drywall installation, repair, finishing & texture matching across Sacramento and surrounding areas.',
    legal: '© Evolve Drywall · Licensed & Insured · Built on Agent9',
  },
};

export default evolveDrywallConfig;
